import { type SetupStep1Input, setupStep1Schema } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import {
  formatFrNationalGroupedDigits,
  normalizeFrPhoneToNationalDigits,
} from '../../../lib/fr-phone-format';
import { DemoPrefillButton } from '../../demo/components/DemoPrefillButton';
import { useSetupWizardStore } from '../store/setup-wizard.store';

export function SetupStep1Form() {
  const { step1, setStep1 } = useSetupWizardStore();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SetupStep1Input>({
    resolver: zodResolver(setupStep1Schema),
    defaultValues: {
      alias: step1.alias ?? '',
      password: step1.password ?? '',
      firstName: step1.firstName ?? '',
      lastName: step1.lastName ?? '',
      email: step1.email ?? '',
      phone: normalizeFrPhoneToNationalDigits(step1.phone ?? ''),
    },
  });

  return (
    <form
      className="space-y-6"
      noValidate
      onSubmit={handleSubmit((data) => {
        setStep1(data);
      })}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            Étape 1 — Qui êtes-vous ?
          </h2>
          <p className="mt-2 text-[0.9rem] leading-relaxed text-slate-600">
            Créez votre compte super-administrateur. Vous pourrez ensuite créer les autres
            utilisateurs.
          </p>
        </div>
        <DemoPrefillButton
          context="setup-step1"
          onPrefill={(data) => {
            for (const [k, v] of Object.entries(data)) {
              if (k === 'phone' && typeof v === 'string') {
                setValue('phone', normalizeFrPhoneToNationalDigits(v), { shouldDirty: true });
              } else {
                setValue(k as keyof SetupStep1Input, v as never, { shouldDirty: true });
              }
            }
          }}
        />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="firstName" className="app-label">
            Prénom
          </label>
          <input id="firstName" className="app-input" {...register('firstName')} />
          {errors.firstName && <p className="text-error mt-2">{errors.firstName.message}</p>}
        </div>
        <div>
          <label htmlFor="lastName" className="app-label">
            Nom
          </label>
          <input id="lastName" className="app-input" {...register('lastName')} />
          {errors.lastName && <p className="text-error mt-2">{errors.lastName.message}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="alias" className="app-label">
          Alias (identifiant de connexion)
        </label>
        <input
          id="alias"
          autoCapitalize="none"
          autoComplete="username"
          className="app-input"
          {...register('alias')}
        />
        {errors.alias && <p className="text-error mt-2">{errors.alias.message}</p>}
      </div>

      <div>
        <label htmlFor="password" className="app-label">
          Mot de passe (12 caractères minimum)
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          className="app-input"
          {...register('password')}
        />
        {errors.password && <p className="text-error mt-2">{errors.password.message}</p>}
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="email" className="app-label">
            E-mail (optionnel)
          </label>
          <input id="email" type="email" className="app-input" {...register('email')} />
          {errors.email && <p className="text-error mt-2">{errors.email.message}</p>}
        </div>
        <div>
          <label htmlFor="phone" className="app-label">
            Téléphone (optionnel)
          </label>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <input
                id="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="06 12 34 56 78"
                className="app-input"
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                value={field.value ? formatFrNationalGroupedDigits(field.value) : ''}
                onChange={(e) => {
                  const d = normalizeFrPhoneToNationalDigits(e.target.value);
                  field.onChange(d === '' ? '' : d);
                }}
              />
            )}
          />
          {errors.phone && <p className="text-error mt-2">{errors.phone.message}</p>}
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          Suivant : informations entreprise
        </button>
      </div>
    </form>
  );
}
